import API_BASE_URL from 'src/constants';
import { useState, useEffect, useCallback } from 'react';
import { Address } from 'src/interfaces/shared';
import { toast } from 'react-toastify';
import i18n from 'src/localization/i18n';
import logger from 'src/utils/logger';
import { handleUnauthorized } from './authToken';

export type SocialProfile = {
  handle: string;
  type: string;
  url: string;
};

export type Company = {
  address: Address;
  email: string | null;
  name: string | null;
  phone: string | null;
  social_profiles: SocialProfile[];
  tax_id: string | null;
  website: string | null;
  tagline?: string | null;
  currency?: string | null;
};

export const companyDataPlaceholder: Company = {
  address: {
    city: null,
    country_code: null,
    country_name: null,
    street: null,
    street2: null,
    zip_code: null,
  },
  email: null,
  name: null,
  phone: null,
  social_profiles: [],
  tax_id: null,
  website: null,
  tagline: null,
};

const useCompany = () => {
  const accessToken = localStorage.getItem('access_token');
  const [company, setCompany] = useState<Company>(companyDataPlaceholder);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCompany = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}portal/company`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized(accessToken);
        }
        throw new Error('Failed to fetch profile data');
      }

      const data = await response.json();
      setCompany(data);
    } catch (err) {
      logger.error('Failed to fetch company data', err);
      setError('Failed to fetch profile data');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  const updateCompanyData = useCallback(
    async (companyData: Company) => {
      logger.info('Updating company data');
      try {
        const response = await fetch(`${API_BASE_URL}portal/company`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(companyData),
        });

        if (!response.ok) {
          throw new Error('Failed to save profile data');
        }
        toast.success(i18n.t('success.company_data_updated'));
        await fetchCompany();
      } catch (error) {
        logger.error('Failed to update company data', error);
        toast.error(i18n.t('errors.company_update_failed'));
      }
    },
    [accessToken, fetchCompany],
  );

  return { company, loading, error, updateCompanyData };
};

export default useCompany;

export const useCompanyLogo = () => {
  const accessToken = localStorage.getItem('access_token');
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCompanyLogo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}portal/company/logo`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch profile logo');
      }

      const blob = await response.blob();
      const imageUrl = URL.createObjectURL(blob);
      setCompanyLogo(imageUrl);
    } catch (err) {
      logger.error('Failed to fetch company logo', err);
      setError('Failed to fetch profile logo');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  const updateCompanyLogo = useCallback(
    async (file: File | null) => {
      if (!file) return;
      logger.info('Updating company logo');
      const formData = new FormData();
      formData.append('image', file);

      try {
        const response = await fetch(`${API_BASE_URL}portal/company/logo`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        });

        if (!response.ok) {
          throw new Error('Failed to update profile picture');
        }
        toast.success(i18n.t('success.company_logo_updated'));
        fetchCompanyLogo();
      } catch (err) {
        logger.error('Failed to update company logo', err);
        setError('Failed to update profile picture');
        toast.error(i18n.t('errors.company_logo_update_failed'));
      }
    },
    [accessToken, fetchCompanyLogo],
  );

  useEffect(() => {
    fetchCompanyLogo();
  }, [fetchCompanyLogo]);

  return { companyLogo, loading, error, updateCompanyLogo };
};
